import { Annotation } from './annotation.types';

export class ModalCanvasRenderer {
  static getAnnotationCorners(ann: Annotation): Array<{ x: number; y: number }> {
    if (ann.type === 'rect') {
      return [
        { x: ann.x, y: ann.y },
        { x: ann.x + ann.width, y: ann.y },
        { x: ann.x + ann.width, y: ann.y + ann.height },
        { x: ann.x, y: ann.y + ann.height },
      ];
    }
    return ann.points;
  }

  static isPointInAnnotation(cx: number, cy: number, ann: Annotation): boolean {
    if (ann.type === 'rect') {
      return cx >= ann.x && cx <= ann.x + ann.width && cy >= ann.y && cy <= ann.y + ann.height;
    }
    const pts = ann.points;
    let inside = false;
    for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
      if (
        ((pts[i].y > cy) !== (pts[j].y > cy)) &&
        (cx < ((pts[j].x - pts[i].x) * (cy - pts[i].y)) / (pts[j].y - pts[i].y) + pts[i].x)
      ) {
        inside = !inside;
      }
    }
    return inside;
  }

  static hexToRgba(hex: string, alpha: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  static redrawCanvas(ctx: any): void {
    const canvas = ctx.annotationCanvasRef?.nativeElement;
    if (!canvas) return;
    const c = canvas.getContext('2d');
    if (!c) return;

    c.clearRect(0, 0, canvas.width, canvas.height);

    // Scale stroke widths with zoom so overlays remain readable at all levels.
    const lw = Math.max(1, 2 / ctx.zoomLevel);

    for (let annIdx = 0; annIdx < ctx.annotations.length; annIdx++) {
      const ann = ctx.annotations[annIdx];
      const isVisible = typeof ctx.isAnnotationVisible === 'function'
        ? ctx.isAnnotationVisible(ann)
        : ann.isVisibleOnCanvas !== false;
      if (!isVisible) {
        continue;
      }
      const annColor = ann.color || '#00e676';
      c.save();
      c.strokeStyle = annColor;
      c.fillStyle = this.hexToRgba(annColor, 0.15);
      c.lineWidth = lw;
      if (ann.type === 'rect') {
        c.strokeRect(ann.x, ann.y, ann.width, ann.height);
        c.fillRect(ann.x, ann.y, ann.width, ann.height);
      } else if (ann.type === 'polygon') {
        c.beginPath();
        c.moveTo(ann.points[0].x, ann.points[0].y);
        for (let i = 1; i < ann.points.length; i++) {
          c.lineTo(ann.points[i].x, ann.points[i].y);
        }
        c.closePath();
        c.stroke();
        c.fill();
      } else if (ann.type === 'brush') {
        c.beginPath();
        c.moveTo(ann.points[0].x, ann.points[0].y);
        for (let i = 1; i < ann.points.length; i++) {
          c.lineTo(ann.points[i].x, ann.points[i].y);
        }
        c.closePath();
        c.stroke();
        c.fill();
      }
      {
        const showClassName = typeof ctx.isAnnotationClassNameVisible === 'function'
          ? ctx.isAnnotationClassNameVisible(ann)
          : ann.isCanvasLabelVisible !== false;
        if (!showClassName) {
          c.restore();
          continue;
        }
        // Draw a small label badge near the first shape point/corner.
        const labelText = ann.label ? `${annIdx + 1}. ${ann.label}` : `${annIdx + 1}.`;
        const fontSize = Math.max(12, 14 / ctx.zoomLevel);
        c.font = `bold ${fontSize}px sans-serif`;
        const metrics = c.measureText(labelText);
        const textX = ann.type === 'rect' ? ann.x + 4 : ann.points[0].x + 4;
        const textY = ann.type === 'rect' ? ann.y + fontSize + 4 : ann.points[0].y + fontSize + 4;
        c.fillStyle = 'rgba(0, 0, 0, 0.65)';
        c.fillRect(textX - 2, textY - fontSize, metrics.width + 6, fontSize + 4);
        c.fillStyle = annColor;
        c.fillText(labelText, textX, textY);
      }
      c.restore();
    }

    if (ctx.showAiPredictions) {
      const boxes: Array<{ x1: number; y1: number; x2: number; y2: number }> = ctx.aiPredictionBoxes ?? [];
      const aiLw = Math.max(1, 2 / ctx.zoomLevel);
      const fontSize = Math.max(11, 13 / ctx.zoomLevel);
      for (const box of boxes) {
        c.save();
        c.strokeStyle = '#ff1744';
        c.fillStyle = 'rgba(255, 23, 68, 0.12)';
        c.lineWidth = aiLw;
        c.setLineDash([6 / ctx.zoomLevel, 4 / ctx.zoomLevel]);
        const w = box.x2 - box.x1;
        const h = box.y2 - box.y1;
        c.strokeRect(box.x1, box.y1, w, h);
        c.fillRect(box.x1, box.y1, w, h);
        c.setLineDash([]);
        c.font = `bold ${fontSize}px sans-serif`;
        const label = 'Detected';
        const metrics = c.measureText(label);
        c.fillStyle = 'rgba(0, 0, 0, 0.65)';
        c.fillRect(box.x1, box.y1 - fontSize - 4, metrics.width + 8, fontSize + 4);
        c.fillStyle = '#ff1744';
        c.fillText(label, box.x1 + 4, box.y1 - 4);
        c.restore();
      }
    }

    if (ctx.pendingAnnotation) {
      // Pending annotation is preview-only until user confirms label.
      const lw2 = Math.max(1, 2 / ctx.zoomLevel);
      c.save();
      c.strokeStyle = '#ffab40';
      c.fillStyle = 'rgba(255, 171, 64, 0.15)';
      c.lineWidth = lw2;
      c.setLineDash([6 / ctx.zoomLevel, 3 / ctx.zoomLevel]);
      if (ctx.pendingAnnotation.type === 'rect') {
        c.strokeRect(ctx.pendingAnnotation.x, ctx.pendingAnnotation.y, ctx.pendingAnnotation.width, ctx.pendingAnnotation.height);
        c.fillRect(ctx.pendingAnnotation.x, ctx.pendingAnnotation.y, ctx.pendingAnnotation.width, ctx.pendingAnnotation.height);
      } else if (ctx.pendingAnnotation.type === 'polygon') {
        c.beginPath();
        c.moveTo(ctx.pendingAnnotation.points[0].x, ctx.pendingAnnotation.points[0].y);
        for (let i = 1; i < ctx.pendingAnnotation.points.length; i++) {
          c.lineTo(ctx.pendingAnnotation.points[i].x, ctx.pendingAnnotation.points[i].y);
        }
        c.closePath();
        c.stroke();
        c.fill();
      } else if (ctx.pendingAnnotation.type === 'brush') {
        c.beginPath();
        c.moveTo(ctx.pendingAnnotation.points[0].x, ctx.pendingAnnotation.points[0].y);
        for (let i = 1; i < ctx.pendingAnnotation.points.length; i++) {
          c.lineTo(ctx.pendingAnnotation.points[i].x, ctx.pendingAnnotation.points[i].y);
        }
        c.closePath();
        c.stroke();
        c.fill();
      }
      c.setLineDash([]);
      c.restore();
    }

    if (ctx.inProgressRect) {
      const lw3 = Math.max(1, 2 / ctx.zoomLevel);
      const { startX, startY, endX, endY } = ctx.inProgressRect;
      c.strokeStyle = '#00e676';
      c.fillStyle = 'rgba(0, 230, 118, 0.15)';
      c.lineWidth = lw3;
      c.setLineDash([6 / ctx.zoomLevel, 4 / ctx.zoomLevel]);
      c.strokeRect(startX, startY, endX - startX, endY - startY);
      c.fillRect(startX, startY, endX - startX, endY - startY);
      c.setLineDash([]);
    }

    if (ctx.inProgressPolygon && ctx.inProgressPolygon.length > 0) {
      const lw4 = Math.max(1, 2 / ctx.zoomLevel);
      c.strokeStyle = '#00e676';
      c.lineWidth = lw4;
      c.setLineDash([6 / ctx.zoomLevel, 4 / ctx.zoomLevel]);
      c.beginPath();
      c.moveTo(ctx.inProgressPolygon[0].x, ctx.inProgressPolygon[0].y);
      for (let i = 1; i < ctx.inProgressPolygon.length; i++) {
        c.lineTo(ctx.inProgressPolygon[i].x, ctx.inProgressPolygon[i].y);
      }
      if (ctx.polygonMousePos) {
        // Live edge preview from the last fixed point to current pointer.
        c.lineTo(ctx.polygonMousePos.x, ctx.polygonMousePos.y);
      }
      c.stroke();
      c.setLineDash([]);

      const dotR = Math.max(3, 4 / ctx.zoomLevel);
      for (const pt of ctx.inProgressPolygon) {
        c.beginPath();
        c.arc(pt.x, pt.y, dotR, 0, Math.PI * 2);
        c.fillStyle = '#00e676';
        c.fill();
      }
      c.fillStyle = 'rgba(0, 230, 118, 0.15)';
    }

    if (ctx.inProgressBrush && ctx.inProgressBrush.length > 0) {
      const liveHalf = this.getBrushHalfWidth(ctx);
      c.save();
      c.lineCap = 'round';
      c.lineJoin = 'round';
      c.lineWidth = liveHalf * 2;
      c.strokeStyle = 'rgba(0, 230, 118, 0.75)';
      c.fillStyle = 'rgba(0, 230, 118, 0.75)';
      if (ctx.inProgressBrush.length === 1) {
        c.beginPath();
        c.arc(ctx.inProgressBrush[0].x, ctx.inProgressBrush[0].y, liveHalf, 0, Math.PI * 2);
        c.fill();
      } else {
        c.beginPath();
        c.moveTo(ctx.inProgressBrush[0].x, ctx.inProgressBrush[0].y);
        for (let i = 1; i < ctx.inProgressBrush.length; i++) {
          c.lineTo(ctx.inProgressBrush[i].x, ctx.inProgressBrush[i].y);
        }
        c.stroke();
      }
      c.restore();
    }

    if (ctx.activeTool === 'rect' && ctx.rectCursorPos && !ctx.inProgressRect) {
      const { x, y } = ctx.rectCursorPos;
      const canvas = ctx.annotationCanvasRef?.nativeElement;
      const w = canvas?.width ?? 0;
      const h = canvas?.height ?? 0;
      const dashLen = 6 / ctx.zoomLevel;
      const gapLen = 4 / ctx.zoomLevel;
      const lw = Math.max(0.5, 1.5 / ctx.zoomLevel);
      c.save();
      // White outer line for visibility on dark backgrounds.
      c.strokeStyle = 'rgba(255, 255, 255, 0.85)';
      c.lineWidth = lw * 2;
      c.setLineDash([dashLen, gapLen]);
      c.beginPath();
      c.moveTo(0, y);
      c.lineTo(w, y);
      c.moveTo(x, 0);
      c.lineTo(x, h);
      c.stroke();
      // Dark inner line for visibility on light backgrounds.
      c.strokeStyle = 'rgba(0, 0, 0, 0.5)';
      c.lineWidth = lw;
      c.setLineDash([dashLen, gapLen]);
      c.beginPath();
      c.moveTo(0, y);
      c.lineTo(w, y);
      c.moveTo(x, 0);
      c.lineTo(x, h);
      c.stroke();
      c.setLineDash([]);
      c.restore();
    }

    if (ctx.activeTool === 'brush' && ctx.brushCursorPos && !ctx.inProgressBrush) {
      const r = this.getBrushHalfWidth(ctx);
      c.save();
      c.beginPath();
      c.arc(ctx.brushCursorPos.x, ctx.brushCursorPos.y, r, 0, Math.PI * 2);
      c.strokeStyle = 'rgba(255, 255, 255, 0.9)';
      c.lineWidth = Math.max(0.5, 1.5 / ctx.zoomLevel);
      c.stroke();
      c.beginPath();
      c.arc(ctx.brushCursorPos.x, ctx.brushCursorPos.y, r, 0, Math.PI * 2);
      c.strokeStyle = 'rgba(0, 0, 0, 0.5)';
      c.lineWidth = Math.max(0.5, 1 / ctx.zoomLevel);
      c.setLineDash([3 / ctx.zoomLevel, 3 / ctx.zoomLevel]);
      c.stroke();
      c.setLineDash([]);
      c.restore();
    }

    if (ctx.activeTool === 'edit-points') {
      type EditPointEntry = {
        ai: number;
        pi: number;
        x: number;
        y: number;
        color: string;
      };
      const pointGroups = new Map<string, EditPointEntry[]>();

      for (let ai = 0; ai < ctx.annotations.length; ai++) {
        const ann = ctx.annotations[ai];
        const pts = this.getAnnotationCorners(ann);
        const annColor = ann.color || '#00e676';
        for (let pi = 0; pi < pts.length; pi++) {
          const key = `${Math.round(pts[pi].x)}:${Math.round(pts[pi].y)}`;
          // Group rounded coordinates so overlapping corners can be drawn as stacks.
          const group = pointGroups.get(key) || [];
          group.push({ ai, pi, x: pts[pi].x, y: pts[pi].y, color: annColor });
          pointGroups.set(key, group);
        }
      }

      for (const group of pointGroups.values()) {
        // Render hovered point last so it stays visually on top.
        group.sort((a, b) => {
          const aHovered = a.ai === ctx.hoverAnnIdx && a.pi === ctx.hoverPointIdx;
          const bHovered = b.ai === ctx.hoverAnnIdx && b.pi === ctx.hoverPointIdx;
          return Number(aHovered) - Number(bHovered);
        });

        const baseR = Math.max(4, 4 / ctx.zoomLevel);
        const stackStep = Math.max(1, 1.5 / ctx.zoomLevel);
        for (let si = 0; si < group.length; si++) {
          const pt = group[si];
          const isHovered = pt.ai === ctx.hoverAnnIdx && pt.pi === ctx.hoverPointIdx;
          const hoverBoost = isHovered ? Math.max(2, 2 / ctx.zoomLevel) : 0;
          const r = baseR + si * stackStep + hoverBoost;
          c.save();
          c.beginPath();
          c.arc(pt.x, pt.y, r, 0, Math.PI * 2);
          c.fillStyle = isHovered ? '#fff' : pt.color;
          c.fill();
          c.strokeStyle = pt.color;
          c.lineWidth = Math.max(1, 1.5 / ctx.zoomLevel);
          c.stroke();
          c.restore();
        }
      }
    }

    if (ctx.activeTool === 'move-shape' && ctx.hoverAnnIdx !== -1 && ctx.moveAnnIdx === -1) {
      const ann = ctx.annotations[ctx.hoverAnnIdx];
      c.save();
      c.strokeStyle = '#fff';
      c.lineWidth = Math.max(2, 3 / ctx.zoomLevel);
      c.setLineDash([5 / ctx.zoomLevel, 3 / ctx.zoomLevel]);
      if (ann.type === 'rect') {
        c.strokeRect(ann.x, ann.y, ann.width, ann.height);
      } else if (ann.type === 'polygon' || ann.type === 'brush') {
        c.beginPath();
        c.moveTo(ann.points[0].x, ann.points[0].y);
        for (let i = 1; i < ann.points.length; i++) c.lineTo(ann.points[i].x, ann.points[i].y);
        c.closePath();
        c.stroke();
      }
      c.setLineDash([]);
      c.restore();
    }
  }

  static getBrushHalfWidth(ctx: any): number {
    const canvas = ctx.annotationCanvasRef?.nativeElement;
    const img = ctx.theImageRef?.nativeElement;
    if (!canvas || !img || !img.clientWidth) return ctx.brushSize / 2;
    return (ctx.brushSize / 2) * canvas.width / (ctx.zoomLevel * img.clientWidth);
  }
}
